import { fn, INamed } from '../utils';
import { NavPageItem, IPageQueryConstraint, NavPropertyItem } from "../navdata";
import { CompatibilityService } from "../services/compatibilityservice";
import { TNavUrlSource, IHasNavUrl, ResolveNavUrl, TNavUrlAccess, INavUrlInfo } from '../services/navurlservice';
import {
    IFDRW_WFInstance, IIAX_WF_Archive_Document_Sub, IIAX_WF_Documents_Sub, Pages,
    Type_10, IFDRW_Workflow_Setup_WS, IFDRW_Workflow_User_Instances_List, IFDDI_LuGM_Geschäftsvorfälle,
    IFDDI_DMS_Purchase_Document, IFDDI_DMS_Sales_Document, IIAX_LuGM_Zusatzdaten_Line, Type, EventType,
    IFDRW_Workflow_Instance_Action_List, IFDRW_WF_Action_Fwd_Comments_List, IFDRW_Workflow_Step_List, IFDRW_Wizard, IFDDI_Wizard_Create_New_DMS_Document, AccountingMode, Feststellung
} from "./rwfpages.generated";
import { RwfAutoResolver } from './rwf.backend';
import { ROWMETHOD, PROP, FDRW_WFInstance, FDDI_DMS_Sales_Document, METHOD, FDRW_Auftragsbuchblatt, FDRW_Auftrag, FDRW_Anordnungsblatt, FDDI_DMS_Purchase_Document, FDRW_Wizard, FDDI_Wizard_Create_New_DMS_Document } from './rwf.const';
import { IKWFSignDoc, IKWFSignOptions } from '@common/services/DialogService';
import { IRWF3DocLink } from '@common/preview/preview.shared';
import { WrapAsObservable, ValidateNotKendoMobile } from '@common/navkendo';
import { NavPropertyItems, NavPageItems } from '@common/navdata';
import { IBrigdeOptions } from 'src/extern/csBridge';
import { DisplayPageItem } from './rwf.edit';

const WFInstance = Pages.FDRW_WFInstance;
const MSG_MISSING_DATA_SETRA = "Die replizierten Beleginformationen für einfache Workflowbenutzer konnten nicht bestimmt werden. Es werden die Daten der Vorkontierung angezeigt!";
/** Promise to Set Current Mandant; CAUTION : Reload necessary after success if already initialized */
export function SetCurrentCompany(this: void, $c: CompatibilityService, fullUrl: string | INavUrlInfo) {
  const currInfo = ResolveNavUrl(fullUrl, true);
  const $rwfBackend = $c.resolve("Rwf")("RwfBackend");
  return $rwfBackend.BootstrapRwf(currInfo).then((customBootstrap) => {
    return $c.resolve("NavUrl").NavUrl.then(currentUrl => {
      return $c.resolve("Routing").SetCurrentNavContext(customBootstrap.NavUrlInfo, currentUrl, true);
    });
  });
}

/** Possible Parameters to resolve a Workflow Instance from */
export type TWFInstanceID = string | number | IFDRW_Workflow_User_Instances_List | NavPageItem<IFDRW_WFInstance>
  | PromiseLike<string | number | IFDRW_Workflow_User_Instances_List | NavPageItem<IFDRW_WFInstance>>;

/** Possible Wizard PageItems (depends on 'AccountingMode' of WFInstance Item) */
export type TWizardPageItem = NavPageItem<IFDRW_Wizard> | NavPageItem<IFDDI_Wizard_Create_New_DMS_Document>;

/** Load WorkflowInstance from Parameters, throws error if not found */
export function LoadWfInstance(
  $c: CompatibilityService,
  /** Workflow Instance Parameter To resolve */
  wfInstanceId: TWFInstanceID,
  /** Optional Explicit NavUrl Parameter */
  _NavUrl: TNavUrlSource = undefined
) {
  const [NavClient, NavUrl] = [$c.resolve("NavClient"), $c.resolve("NavUrl")];

  return $c.when(wfInstanceId).then(wfInstanceParam => {
    if (wfInstanceParam instanceof NavPageItem) {
      if (wfInstanceParam.PageName != FDRW_WFInstance) {
        throw new Error(`Expected PageName '${FDRW_WFInstance}', got '${wfInstanceParam.PageName}'`);
      }
      return wfInstanceParam;
    }

    return $c.when(ResolveNavUrl(_NavUrl || <IHasNavUrl>NavUrl, true)).then(navUrlInfo => {
      const wfParam = "" + wfInstanceParam;
      const isInt = /^[0-9]+$/.test(wfParam);

      return $c.when().then(() => {
        if (isInt)
          return NavClient.LoadItemsSeperate<IFDRW_WFInstance>({
            PageName: FDRW_WFInstance,
            PageCount: 2,
            Constraints: [{ FieldName: "EntryNo", Value: wfParam }]
          }, navUrlInfo.FullUrl, false)
            .then(data => {
              return data.length == 1 && data[0];
            });
        else {
          return NavClient.LoadItem<IFDRW_WFInstance>({
            PageName: FDRW_WFInstance,
            NavUrl: navUrlInfo.FullUrl,
            Keys: { ID: wfParam }
          }, false);
        }
      }).then(pageItem => {
        if (!pageItem) {
          throw new Error(`Workflow "${wfInstanceParam}" konnte nicht gefunden werden.`);
        }
        return pageItem;
      }, err => {
        throw new Error(`Das Laden des Workflows '${wfInstanceParam}' ist fehlgeschlagen: ` + fn.resolveToError(err));
      });
    });
  });
}

/** Load Workflow Instance by InstanceNo or No (WITH META - Description: we Display Dos, Logs : need captions) */
export function OpenWFInstance(this: void,
  rwf: RwfAutoResolver,
  wfInstanceId: TWFInstanceID,
  forceOpen?: boolean,
  _navUrl?: TNavUrlAccess) {
  const $c = rwf("$c");
  return LoadWfInstance($c, wfInstanceId, _navUrl).then(item => {
    var previousCheckoutUser = item.GetValue("CheckedOutBy", true) || "";
    if (forceOpen == false) {
      return $c.when({
        Opened: false,
        Item: item,
        Error: undefined,
        PreviousCheckedOutBy: previousCheckoutUser
      });
    }

    let executeOpenIfNecessary = () => <PromiseLike<any>>item.ExecuteMethod(WFInstance.Methods.Open);
    // Problem : "Open" Modifies, might be cancelled if already Open by user in another "Window"
    if (previousCheckoutUser) {
      executeOpenIfNecessary = () => $c.when(rwf("RwfBootstrap").Resolve).then(bootstrap => {
        const userInfo = bootstrap.RwfUser;
        return !userInfo || userInfo.ID !== previousCheckoutUser;
      }, err => true).then((needsOpen) => {
        if (needsOpen)
          return item.ExecuteMethod(WFInstance.Methods.Open);
      });
    }
    return executeOpenIfNecessary().then(() => {
      return {
        Opened: true,
        Item: item,
        Error: <string>undefined,
        PreviousCheckedOutBy: previousCheckoutUser
      };
    }, err => {
      if (!forceOpen)
        throw err;

      var instanceId = item.GetValue("ID", true);
      // If Open Failed, check availability of Instance for User
      return $c.resolve("NavClient").Read<IFDRW_Workflow_User_Instances_List>({ PageName: Pages.FDRW_Workflow_User_Instances_List.PageName, Keys: { ID: instanceId }, NavUrl: item.ClientInfo.Url }).then(r => {
        // not available, throw orig error
        if (!(r && r.Data))
          throw err;

        return {
          Item: item,
          Opened: false,
          Error: fn.resolveToError(err),
          PreviousCheckedOutBy: previousCheckoutUser
        };
      }, innerErr => { throw err; });
    });
  });
}

/** Close Workflow Instance (opt. Setting Custom Routeing Parameter  useful iif IsZwischenspeichern */
export function CloseWorkflowInstance(this: void, kwfInstance: NavPageItem<IFDRW_WFInstance>, routeInfo: {
  IsZwischenSpeichern: boolean;
  Action?: IFDRW_Workflow_Instance_Action_List;
  Comment?: IFDRW_WF_Action_Fwd_Comments_List;
  Remark?: string
}) {
  if (!kwfInstance || !kwfInstance.ClientInfo || !(kwfInstance.ClientInfo.MetaData.PageName == FDRW_WFInstance))
    throw new Error("RouteWorkflowInstance : invalide Instance Item");

  // Collect Properties to Set On WFInstance before Update
  if (routeInfo.Action) {
    var propInfo: Partial<IFDRW_WFInstance> = fn.ToDictionaryEx(Object.getOwnPropertyNames(Transfer2WFInstance),
      item => Transfer2WFInstance[item], item => routeInfo.Action[item] || null); // null as default : otherwise no reset triggered!
  } else {
    propInfo = {};
  }

  if (routeInfo.Comment)
    propInfo.FWCommentCode = (routeInfo.Comment && routeInfo.Comment.WorkflowForwardingComment) || null;
  if (routeInfo.Remark != undefined)
    propInfo.Remark = routeInfo.Remark || "";

  return kwfInstance.SetValues(Object.getOwnPropertyNames(propInfo), p => propInfo[p]).then(item =>
    kwfInstance.Update().then(() =>
      kwfInstance.ExecuteMethod(WFInstance.Methods.Close(kwfInstance.Key, routeInfo.IsZwischenSpeichern))));
}

/** Add Workflow Log Entry for RWF3  */
export function LogReauthenticate(instance: NavPageItem) {
  return instance.ExecuteMethod(WFInstance.Methods.CreateWorkflowLogEntry(instance.Key,
    Type.functional, EventType.Reauthenticate, "Benutzerauthentifizierung erfolgreich"));
}

/** Force Use Reauthentication  */
export function ReAuthenticate(this: void, $c: CompatibilityService, message?: string) {
  return $c.resolve("Rwf")("RwfBootstrap").Resolve.then(rwf => {
    const settings = rwf.RwfSettings;
    if (settings.ReauthenticateSpanInSeconds > 0) {
      var storage: { _LastAuth: Date } = <any>settings;
      if (storage._LastAuth) {
        var diff = Math.abs(Math.round(((new Date()).getTime() - storage._LastAuth.getTime()) / 1000));
        if (diff <= settings.ReauthenticateSpanInSeconds) {
          console.log(`ReAuthenticate ignored, last sucessfull reauthentication ${diff} (<= ${settings.ReauthenticateSpanInSeconds}) seconds ago.`);
          return $c.when();
        }
      }
    }
    return $c.resolve("DialogService").ShowInput({
      message: message || "Für die Weiterleitung ist eine erneute Eingabe Ihres Passworts notwendig!",
      title: "Benutzervalidierung",
      inputText: "",
      isPassword: true
    }).then((input) => {
      if (!input.okPressed)
        throw fn.error.UICancel();

      return $c.resolve("NavClient").ValidateUserPwd(input.inputString).then(() => {
        if (storage) {
          storage._LastAuth = new Date();
        }
      });
    });
  });
}

/** Route A WorkflowInstance with given Parameters, either as Save, or as Route */
export function RouteWorkflowInstance(this: void, kwfInstance: NavPageItem<IFDRW_WFInstance>, routeInfo: {
  Action: IFDRW_Workflow_Instance_Action_List;
  Comment: IFDRW_WF_Action_Fwd_Comments_List;
  Remark: string
}) {
  return SaveRoutingInfo(kwfInstance, routeInfo).then(() =>
    kwfInstance.ExecuteMethod(WFInstance.Methods.Route).then(fn.identity, ThrowGracefullError));
}
/** Persist Current Routing Info to NAV */
export function SaveRoutingInfo(this: void, kwfInstance: NavPageItem, routeInfo: {
  Action: IFDRW_Workflow_Instance_Action_List;
  Comment: IFDRW_WF_Action_Fwd_Comments_List;
  Remark: string
}) {
  if (!kwfInstance || !kwfInstance.ClientInfo || !(kwfInstance.ClientInfo.MetaData.PageName == Pages.FDRW_WFInstance.PageName))
    throw new Error("RouteWorkflowInstance : invalide Instance Item");

  const $c = kwfInstance.Anchor.Service.$c;
  if (!routeInfo || !routeInfo.Action || !routeInfo.Action.No)
    return $c.reject("Weiterleiten nur mit gewählter Weiterleitungsaktion möglich");

  // Collect Properties to Set On WFInstance before Update
  var propInfo: Partial<IFDRW_WFInstance> = fn.ToDictionaryEx(Object.getOwnPropertyNames(Transfer2WFInstance),
    item => Transfer2WFInstance[item], item => routeInfo.Action[item] || null); // null as default : otherwise no reset triggered!

  propInfo.FWCommentCode = (routeInfo.Comment && routeInfo.Comment.WorkflowForwardingComment) || null;
  propInfo.Remark = routeInfo.Remark || "";

  return kwfInstance.SetValues(Object.getOwnPropertyNames(propInfo),
    p => propInfo[p], false).then(() => kwfInstance.Update());
}

export function IsMainDocSignatureForced(r: IFDRW_Workflow_Setup_WS, c: { Feststellung: Feststellung }) {
  return r?.SignatureEnabled && r?.SignatureEnabledOnMainDocument == true && [Feststellung.rechnerische_Richtigkeit, Feststellung.sachliche_Richtigkeit, Feststellung.sachliche_und_rechnerische_Richtigkeit].indexOf(c?.Feststellung) >= 0;
}


/** UI Interactive Validate Signatures if necessary */
export function ValidateSignature(this: void, $c: CompatibilityService, extSignInfo: { Name: string; ID: string; Instance: NavPageItem; SelectedAction: IFDRW_Workflow_Instance_Action_List; CurrentStep: IFDRW_Workflow_Step_List }[], navUrl: string) {
  const $rwf = $c.resolve("Rwf")("RwfBootstrap");
  const $rwfUser = $rwf.Resolve.then(r => r.RwfUser);
  const $rwfSetup = $rwf.Resolve.then(r => r.RwfSetup);

  var signNeeded = extSignInfo.filter(item => item.SelectedAction?.SignatureMandatory === true);
  var signInfo = $rwfSetup.then(r => {
    const ToID = (item: { ID: string }) => item?.ID;
    if (r.SignatureEnabledOnMainDocument === false) {
      return {
        MainDocIds: [],
        PaymDocIDs: signNeeded.map(ToID)
      };
    } else {
      return {
        MainDocIds: signNeeded.filter(item => IsMainDocSignatureForced(r, item.SelectedAction)).map(ToID),
        PaymDocIDs: signNeeded.filter(item => !IsMainDocSignatureForced(r, item.SelectedAction)).map(ToID)
      };
    }
  });
  ValidateNotKendoMobile();
  return $c.when(signInfo).then(signInfo => {
    var signPaymDocIds = signInfo.PaymDocIDs;

    if (signInfo.PaymDocIDs.length == 0 && signInfo.MainDocIds.length == 0)
      return $c.when();



    // Retrieve Info for which WF's (Re-) Signing is necessary
    return CheckSignatureValid(signPaymDocIds, navUrl).then(signPaymInfo => {
      var instancesToSign = extSignInfo.filter(item => signPaymInfo[item.ID] == false || signPaymDocIds.indexOf(item.ID) < 0);

      var signInfos = instancesToSign.map(sigInstance => {
        var id = sigInstance.ID;
        // at least "\" at the end leads to problems ...
        var name = (sigInstance.Name || sigInstance.ID).replace(/[^\wäöü ]/ig, "_") || sigInstance.ID;
        // Retrieve 'Anordung' for Signing (or MainDoc)
        const IsMainDoc = IsMainDocSignatureForced($rwf.EnsureResolved().RwfSetup, sigInstance.SelectedAction);
        const GetDocMeth = IsMainDoc ? "DownloadMainDocument" : "DownloadPaymentOrder";
        return $c.get<{ Base64Content: string; SignatureCount: number }>(`infoma.Web.KWF3/${GetDocMeth}?` + fn.EncodeQueryData({ instanceId: id, navUrl: navUrl }), {}).then((d) => {
          fn.resolveToError(d && d.data, true);

          var stepInfo = (sigInstance.CurrentStep && sigInstance.CurrentStep.Name) || "";
          var actionInfo = (sigInstance.SelectedAction && sigInstance.SelectedAction.WFActionName) || "";
          var dateInfo = (new Date()).toLocaleDateString().replace(/[^\x00-\x7F]/g, ""); // WTF?!? : Separator is not '.', so remove non ascii chars
          // Download might invalid instance, so Reload the Item ( to Revalidate ClientSide Key)
          return $rwfUser.then(user => sigInstance.Instance.RevertItem(true).then(() => {
            return <IKWFSignDoc & {/** Propagate Context */ ID: string, IsMainDoc: boolean }>{
              ID: id, // see below ...
              IsMainDoc: IsMainDoc,
              Name: name + ".pdf",
              Base64Content: d.data.Base64Content,
              Text: stepInfo + "\n" + actionInfo + "\n" + dateInfo + "\n" + "\${digestsigner.subject.CN}\${entity.nl}\${digestsigner.subject.O}",
              PreviousSignatureCount: d.data.SignatureCount,
              Info: {
                ContactInfo: user.E_Mail || "",
                Location: stepInfo,
                Name: user.ID || "",
                Reason: actionInfo
              }
            };
          }));
        });
      });

      // No Items to sign: Inform User about validity
      if (signInfos.length == 0)
        return $c.resolve("DialogService").ShowDialog("Die angebrachte" + (extSignInfo.length > 1 ? "n Signaturen sind" : " Signatur ist") + " noch gültig, eine erneute Signatur ist nicht erforderlich.");

      const $rwfBootstrap = $rwf.Resolve;

      type OptPromisified<T> = { [name in keyof T]: T[name] | PromiseLike<T[name]> };
      return $c.all(<OptPromisified<IKWFSignOptions>>{
        SerialNo: $rwfBootstrap.then(bootstrap => {
          const user = bootstrap.RwfUser;
          if (bootstrap.RwfSetup.SignatureSerialNoMandatory && !user.SignatureSerialNumber)
            ThrowGracefullError("Signieren ohne hinterlegte Signaturkarteninformation/AliasInformation nicht möglich!");
          return user.SignatureSerialNumber;
        }),
        Bridge: $c.get<string>("/infoma.Web.Nav/Utils/GetLicence").then(r => r.data && <IBrigdeOptions>{
          LicenseBase64: btoa(r.data),
          Select: $rwfBootstrap.then(bootstrap => {
            // TODO only valid for windows auth?
            const userName = bootstrap.RwfSettings.User.LoginName;
                     const userfrags = userName.split('\\');
                     return {
                        user: (userfrags.length > 0 ? userfrags[userfrags.length - 1] : userName).toLowerCase(),
                 }; })}, fn.noop),
            ProGOV: $rwfBootstrap.then(bootstrap => bootstrap.RwfSettings.ProGOV)
        }).then(config => {
            return $c.resolve("DialogService").ModalSignDocument(config, $c.all(signInfos).then(fn.identity)).then(result => {
                if (result == undefined) // Modal Signing Cancelled
                    throw fn.error.ThrowCancel();

          var uploads = result.map(signedInfo => {
            const UploadMeth = signedInfo.IsMainDoc ? "UploadSignedMainDoc" : "UploadPaymentOrderContent";
            return $c.post(`infoma.Web.KWF3/${UploadMeth}?` + fn.EncodeQueryData({ instanceId: signedInfo.ID, navUrl: navUrl }), { Content: signedInfo.Base64Content }).then(r => {
              fn.resolveToError(r && r.data, true);
            }).then(fn.noop, err => {
              // On Problem Uploading PaymentOrder Content:
              // Allow Interception of Current Error to Allow for Continuing the other Signature Operations
              let errMessage = err,
                continueOnerror = false;
              try {
                var errItem = extSignInfo.filter(item => item.ID == signedInfo.ID)[0];
                if (errItem) {
                  let errModel = errItem.Instance.Model,
                    infoId = errModel["EntryNo"] || errItem.ID;
                  errMessage = new Error("Fehler beim Speichern des signierten Dokuments '" + errItem.Name + ", Nr. " + infoId + ":'\n" + err);
                  var e = {
                    ID: errItem.ID, Instance: errItem.Instance, Error: err, Continue: false
                  };
                  errModel.trigger("RWFSignError", e);
                  if (e.Continue === true)
                    continueOnerror = true; // If set to Continue : Interceptor must have removed the Item!
                }
              } finally {
                if (!continueOnerror)
                  // tslint:disable-next-line:no-unsafe-finally
                  throw errMessage;
              }
            });
          });

          return $c.all(uploads).then(() => {
            // "UploadPaymentOrderContent" add LogEntry => Invalidates Instances
            return $c.all(instancesToSign.map(item => item.Instance.RevertItem(true))).then(fn.noop);
          });
        });
      });
    });
  });
  /** Retrieve Valid State of Signature */
  function CheckSignatureValid(this: void, instanceIds: string[], navUrl: string) {
    if (!instanceIds)
      return $c.when(<INamed<boolean>>undefined);
    else if (instanceIds.length == 0)
      return $c.when(<INamed<boolean>>{});

    return $c.post<INamed<boolean>>("infoma.Web.KWF3/IsSignatureValid?" + fn.EncodeQueryData({ navUrl: navUrl }), instanceIds).then(signInfo => {
      fn.resolveToError(signInfo && signInfo.data, true);
      return signInfo.data;
    });
  }
}

/** Execute Posting Check */
export function ExecutePostingCheck(this: void, config: {
  readonly $c: CompatibilityService,
  readonly WFInstance: NavPageItem<IFDRW_WFInstance>,
  readonly DisplayMainLineData?: NavPropertyItems,
  onErr?: (errItems: { Item?: NavPropertyItem, Error: string }[]) => void,
  readonly supressSucessError?: true
}) {
  const WFInstance = config.WFInstance;
  return WFInstance.ExecuteMethod({ MethodName: METHOD.PostingCheck, Parameters: {} }).then(result => {
    if (result["Result"] === true && config.supressSucessError !== true)
      return config.$c.resolve("DialogService").ShowDialog({ title: "Information", message: "Belegprüfung erfolgreich abgeschlossen." });
  }, err => {
    const DisplayMainLineData = config.DisplayMainLineData;
    var check = TryParseLineErrors(err, DisplayMainLineData);
    if (check && check.length) {
      var allErrs = fn.distinct(check.map(c => c.Error));
      var noLineErrs = fn.distinct(check.filter(f => !f.Item).map(c => c.Error));
      if (config.onErr) {
        config.onErr(check);
      }

      if (allErrs.length == 1) {
        throw new Error(allErrs[0]);
      } else {
        const txtErrStart = `${allErrs.length} Fehler${allErrs.length != check.length ? ` in ${check.length} Zeilen` : ``} bei der Belegprüfung`;

        if (noLineErrs.length == 0) {
          throw new Error(`${txtErrStart}.\r\nBitte beachten Sie die Details in den Belegzeilen`);
        } else {
          throw new Error(`${txtErrStart}:\r\n` + noLineErrs.join("\r\n"));
        }
      }
    }
    throw err;
  });
}

/** RegExp for "Graceful" PostingError Marker */
const _PostingError = /##signal:postingerror##/ig;

/** Throw Error Marked as GraceFull */
export function ThrowGracefullError(err: any) {
  var postingError = <string>fn.resolveToError(err, false);
  if (typeof (postingError) == "string" && _PostingError.test(postingError)) {
    // It's a "graceful" PostingError
    postingError = postingError.replace(_PostingError, "Fehler beim Buchen:\n");
    throw fn.error.ExplicitError(postingError);
  }

  throw fn.error.ExplicitError(err);
};

/** Property maps : SelectedAction to WFInstance  */
export var Transfer2WFInstance: Partial<Record<keyof IFDRW_Workflow_Instance_Action_List, keyof IFDRW_WFInstance>> = {
  "WFActionCode": "SelectedAction",
  "WFActionNextStepCode": "DesiredNextStep",
  "WFActionNextPartCode": "DesiredNextPart",
  "WFUserID": "DesiredNextUser"
};

/** Detect Kameral Workflow Setup */
export function IsKameral(setup: IFDRW_Workflow_Setup_WS) {
  return setup && setup.AccountingMode == 1;
}

export function IsPurchOrder(_DisplayItem: NavPageItem) {
  const DisplayItem = _DisplayItem as DisplayPageItem;
  switch (DisplayItem?.PageName) {
    case "FDDI_DMS_Purchase_Document":
            /**blank*/0 |/**Allocation_Template*/1 |/**Order_No*/2 |/**Reservation*/3 |/**RecurringDocument*/4 |/**PurchOrderChange*/5;
      return DisplayItem.GetValue("Creation_Type") == 3 && !!DisplayItem.GetValue("OrderNo");
    case "FDDI_Wizard_Create_New_DMS_Document":
      return DisplayItem.GetValueEx("Creation_Type") == "Reservation" && !!DisplayItem.GetValue("Order_No");
    default:
      return false;
  }
}

export function IsRechnungMitBestellbezug(_DisplayItem: NavPageItem) {
  const DisplayItem = _DisplayItem as DisplayPageItem;
  switch (DisplayItem?.PageName) {
    case "FDDI_DMS_Purchase_Document":
      // == "Invoice" : only for purchHeeader! not wizard!
      return DisplayItem.GetValueEx("Document_Type") == "Invoice" && !!DisplayItem.GetValue("OrderNo");
    case "FDDI_Wizard_Create_New_DMS_Document":
      return DisplayItem.GetValueEx("Creation_Type") == "Order_No" && !!DisplayItem.GetValue("Order_No");
    default:
      return false;
  }
}

export function IsPurchOrderChange(_DisplayItem: NavPageItem) {
  const DisplayItem = _DisplayItem as DisplayPageItem;
  switch (DisplayItem?.PageName) {
    case "FDDI_DMS_Purchase_Document":
            /**blank*/0 |/**Allocation_Template*/1 |/**Order_No*/2 |/**Reservation*/3 |/**RecurringDocument*/4 |/**PurchOrderChange*/5;
      return DisplayItem.GetValue("Creation_Type") == 5 && !!DisplayItem.GetValue("OrderNo");
    case "FDDI_Wizard_Create_New_DMS_Document":
      return DisplayItem.GetValueEx("Creation_Type") == "PurchOrderChange" && !!DisplayItem.GetValue("Order_No");
        default:
            return false;
    }
}

/** Get Common Breakout Open Functionality */
export function OpenBreakoutInNav($c: CompatibilityService, wfInstanceId: TWFInstanceID, _NavUrl: TNavUrlSource = undefined) {
    return LoadWfInstance($c, wfInstanceId, _NavUrl).then(instance => {
      return instance.ExecuteMethod(WFInstance.Methods.GetBreakoutUrlWebClient).then(r => window.open(r["Result"], "_blank"));
    });
}

/** Returns all docs in sorted order (ordered by type) */
export function GetSortedDocs<T extends IIAX_WF_Archive_Document_Sub | IIAX_WF_Documents_Sub>(docs: T[]) {
    // Sort docs
    var sortedDocs: T[] = [],
        docType = Type_10;

    if (docs && docs.length > 0) {
        let dateSort = (a: T, b: T) => { var diff = a.Added.valueOf() - b.Added.valueOf(); return diff; };
    let typeSort = (a: T, b: T) => { return a.Type < b.Type ? -1 : a.Type > b.Type ? 1 : 0; };

    let mainDoc = docs.filter(item => item.Type == docType.Main_Document);
    let paymOrdDoc = docs.filter(item => item.Type == docType.PaymOrd);
    let supplementDocs = docs.filter(item => item.Type == docType.SupDoc || item.Type == docType.CapturingInput).sort(dateSort);
    let tracking = docs.filter(item => item.Type > docType.CapturingInput).sort(typeSort);
    // Makes this blank enum sense?! - Never set!?
    let undefinedDocs = docs.filter(item => item.Type == docType._blank_).sort(dateSort);

    // Ensure main doc title
    if (mainDoc && mainDoc.length > 0 && !mainDoc[0].Description) {
      mainDoc[0].Description = "Hauptdokument";
    }

    sortedDocs = sortedDocs.concat(mainDoc).concat(paymOrdDoc).concat(supplementDocs).concat(tracking).concat(undefinedDocs);
  }

  return sortedDocs;
}

/** Ensure All Lugm Lines are filled for page Item */
export function LugmEnsureLines($c: CompatibilityService, DisplayItem: NavPageItem<IFDDI_DMS_Purchase_Document | IFDDI_DMS_Sales_Document, string>) {
  const NavClient = DisplayItem.Anchor.Service;
  const masterPop: keyof (IFDDI_DMS_Purchase_Document | IFDDI_DMS_Sales_Document) = <any>DisplayItem.ClientInfo.MasterLineProperty;
  if (!masterPop) {
    throw new Error("LugmEnsureLines:Expected Master Prop for " + DisplayItem.ClientInfo.PageName);
  }
  const MainLineData = DisplayItem.GetLineData(masterPop);
  const LugmLineData = DisplayItem.GetLineData("LuGMAddRec");
  if (!MainLineData) {
    throw new Error("LugmEnsureLines: No Line Data found!");
  }
  if (!LugmLineData) {
    throw new Error('LugmEnsureLines: No LuGM Data found!');
  }

  const mainItemsWOutLugm = MainLineData.Items.filter(item => item.GetValue(PROP.AddRecLuGMEntryNo, true) == 0);
  if (mainItemsWOutLugm.length == 0)
    throw new Error("Lugm Zeilen bereits für alle Zeilen verknüpft");
  const Nicht_Zulässig = 2;
  const fields: (keyof IFDDI_LuGM_Geschäftsvorfälle)[] = [
    "Maßnahmenbuchung", "Meldungsbuchung", "Vertragsbuchung", "Mietbuchung", "Erbbaurechtsbuchung", "Raumreservierungsbuchung"
  ];
  //"Buchungsart = <Einkauf/Verkauf>" (je nach Beleg im RWF - meistens sicher Einkauf)
  const constraints: IPageQueryConstraint<IFDDI_LuGM_Geschäftsvorfälle>[] = <any[]>fields.map(field => ({ FieldName: field, Value: Nicht_Zulässig }));
  constraints.push({
    FieldName: "Posting_Type",
    // /**Purchase*/0 |/**Sale*/1 |
    Value: DisplayItem.PageName == FDDI_DMS_Sales_Document ? "1" : "0"
  });

  return NavClient.ReadMultiple<IFDDI_LuGM_Geschäftsvorfälle>({
    PageName: "FDDI_LuGM_Geschäftsvorfälle", PageCount: 2, Constraints: constraints
  }, DisplayItem.ClientInfo).then((data) => {
    const items = data.Data;
    if (items.length != 1) {
      let errMsg = `Es konnte nicht ${items.length > 1
        ? ` eindeutig ` : ""} ermittelt werden, welcher Geschäftsvorfallcode für Zeilen ohne LuGM-Bezug verwendet werden soll.`;
      throw new Error(errMsg);
    }
    const codeToSet = items[0].Geschäftsvorfallcode;
    if (!codeToSet) {
      throw new Error("Kein Geschäftsvorfallcode");
    }
    const multiple = mainItemsWOutLugm.length > 1;
    const question = `Wollen Sie wirklich für ${mainItemsWOutLugm.length} Belegzeile${multiple ? 'n' : ''}, die bislang keine LuGM-Zeile besitz${multiple ? 'en' : 't'}, eine leere LuGM-Zeile mit dem Geschäftsvorfallcode '${codeToSet}' generieren?`;
    return $c.resolve("DialogService").ShowConfirm(question).then((answer) => {
      if (answer === false)
        return;
      if (mainItemsWOutLugm.length == 0) {
        throw new Error('Keine Zeilen ohne LuGM-Bezug gefunden.');
      }

      mainItemsWOutLugm.reduce((prev, currMainItem) => {
        return prev.then(() => {
          return DisplayItem.ExecuteMethod({
            MethodName: ROWMETHOD.InsertLuGMAddRec,
            RowKey: currMainItem.Key,
                        RowProperty: DisplayItem.ClientInfo.MasterLineProperty
                    }).then(() => {
                        var addRecNo = currMainItem.GetValue(PROP.AddRecLuGMEntryNo);
                        if (!addRecNo) {
                            throw new Error("Missing Lugm Reference");
                        }
                        var lugmLine: NavPropertyItem<IIAX_LuGM_Zusatzdaten_Line> = LugmLineData.Items.filter(item => item.Model[PROP.Entry_No] == addRecNo)[0];
                        return lugmLine.SetValue(<keyof IIAX_LuGM_Zusatzdaten_Line>"Geschäftsvorfallcode", codeToSet);

                    });
                });
            }, $c.when());
        });
    });
}

/** Get URL to Download a KWF 3 Document (for running instances) */
export function GetKWF3ArchivedDocumentUrl(wfInstanceId: string, wfDocId?: string, navUrl?: string, fileName?: string) {
  // Must be absolute for external use (GroupDocs, ...)
  var result = fn.GetAbsoluteUrlPrefix(true) + "infoma.Web.KWF3/DownloadArchiveDocument?" + fn.EncodeQueryData({
    wfInstanceId: wfInstanceId,
    wfDocId: wfDocId,
        navUrl: navUrl,
        fileName: fileName
    });
    return result;
}

/** Resolve Nav Client Url and open it */
export function OpenInNav($c: CompatibilityService, recordId: string, navUrl?: string) {
    return $c.when(navUrl || $c.resolve("Rwf")("RwfBootstrap").Resolve.then(rwf => rwf.NavUrlInfo.FullUrl)).then(url => {
        var p = fn.EncodeQueryData({ navUrl: url, recId: recordId });
        return $c.get<{ Url: string }>("infoma.Web.KWF3/GetOpenInNavUrl?" + p).then(d => {
            fn.wOpenExtLink(d.data.Url);
        });
    });
}

/** Get URL to Download a KWF 3 Document (for running instances) */
export function GetKWF3DocumentUrl(wfInstanceId: string, wfDocId: string | undefined, navUrl: string, fileName?: string, asAttachment?: boolean) {
  // Must be absolute for external use (GroupDocs, ...)
  var result = fn.GetAbsoluteUrlPrefix(true) + "infoma.Web.KWF3/DownloadDocument?" + fn.EncodeQueryData({
    wfInstanceId: wfInstanceId,
    wfDocId: wfDocId,
    navUrl: navUrl,
        fileName: fileName,
        asAttachment: !!asAttachment
    });
    return result;
}

export function GetAsDoclink(doc: IIAX_WF_Archive_Document_Sub | IIAX_WF_Documents_Sub, navUrl: string) {
    function IsArchived(item: any): item is IIAX_WF_Archive_Document_Sub {
        const check: IIAX_WF_Archive_Document_Sub = item;
        return check && typeof check.ArchivePath === "string";
    }

    const isArchived = IsArchived(doc);
    if (isArchived) {
        var docUrl = GetKWF3ArchivedDocumentUrl(doc.WorkflowInstanceID, doc.ID, navUrl);
  } else if (doc && doc.WorkflowInstanceID) {
    docUrl = GetKWF3DocumentUrl(doc.WorkflowInstanceID, doc.ID, navUrl);
  }

  if (docUrl) {
    // caution : for maindocument do not set docid, (enforce same url for same documents)
    var docId = isArchived || doc.Type != Type_10.Main_Document ? doc.ID : undefined;
    var doclink: IRWF3DocLink = {
      ID: docId,
      WorkflowInstanceID: doc.WorkflowInstanceID,
      NavUrl: navUrl,
      //AllowEdit: false,
      IsArchived: false,
      Title: doc.Description || '',
      Url: docUrl,
      Type: doc.Type,
      Extension: doc.Extension,
    };
    return doclink;
  }
}

/** Try to map "Per Line" Error Messages to Corresponding lines:
 *  - returns nothing if err not of format for this kind of error or invalid xml
 *  - Line Item in result Container mght be not set if no match!
 */
export function TryParseLineErrors<T>(err: any, items?: NavPropertyItems<T>) {
  const textMessage = fn.resolveToError(err);
  if (!textMessage) {
    return;
  }

  const domParser = new DOMParser();
  try {
    var doc = domParser.parseFromString(textMessage, "text/xml");
  } catch (err) {
    return;
  }
  var errTags = doc.documentElement.getElementsByTagName("ErrorMessage");
  if (errTags.length == 0) {
    return;
  }

  return fn.ToArray(errTags).map(item => {
    // WrapAsObservable : 'GetItems' below will try to match by Client Key
    var result = WrapAsObservable({ Message: "" });
    fn.ToArray(item.childNodes).forEach((c: Element) =>
      result[c.tagName] = c.childNodes && c.childNodes[0] && ("" + c.childNodes[0].nodeValue));
    return result;
  }).map(err => ({ Item: items && items.GetItems(err)[0], Error: err.Message }));
}

// Setup Default Url from KWFApplication Context
const RE_IBAN = /^([a-z]{2}[0-9]{2})([0-9]{4})([0-9]{4})([0-9]{4})([0-9]{2,4})([0-9]*)$/i,
  RE_UNIBAN = /^[0-9a-z ]+$/i;

/** Format Text to desired custom format (spaces) */
export function FormatIBAN(strValue: string) {
  const regExpResult = RE_IBAN.exec("" + strValue);
  return (regExpResult && regExpResult.slice(1).join(" ")) || strValue;
}

/** Unformat Formatted IBAN Value */
export function UnFormatIBAN(strValue: string) {
  if (RE_UNIBAN.test("" + strValue)) {
    return ("" + strValue).split(' ').join('');
  }
  return strValue;
}

/** Load Wizard Item from Workflow Instance */
// TODO : refactory from services "LoadWizardItem" (unify RKamService/DopService)
export function LoadWizardItem(this: void, WFInstance: NavPageItem<IFDRW_WFInstance>): PromiseLike<TWizardPageItem> {
  const accountingMode = WFInstance.GetValue("AccountingMode");
  const wizardPageName = accountingMode == AccountingMode.Kameral ? FDRW_Wizard : FDDI_Wizard_Create_New_DMS_Document;
  const NavClient = WFInstance.Service.$c.resolve("NavClient");
  const guid = WFInstance.Model.ID;
  return NavClient.LoadItem<IFDDI_Wizard_Create_New_DMS_Document | IFDRW_Wizard>({
    PageName: wizardPageName,
    NavUrl: WFInstance.ClientInfo.NavUrl,
    Keys: { GUID: guid },
    InstanceID: WFInstance.Anchor.InstanceID
  }).then(w => {
    if (!w) {
      fn.error("Vorkontierung mit ID '" + guid + "' nicht gefunden.");
    }
    return w;
  }) as PromiseLike<TWizardPageItem>;
}

/** Load Page Item from given Wizard Item (or non if not yet "Weiter zur Kontierung") */
export function LoadPageItemFromWizardItem(this: void, $c: CompatibilityService,
  wizardItem: TWizardPageItem, setraUser: boolean): PromiseLike<NavPageItem | NavPageItems> {
  var pageName = wizardItem && wizardItem.PageName;
  switch (pageName) {
    case FDRW_Wizard:
      return LoadKamPageItem(wizardItem as NavPageItem, setraUser);
    case FDDI_Wizard_Create_New_DMS_Document:
      return LoadDopPageItem(wizardItem as NavPageItem, setraUser);
    default:
      return $c.reject(`LoadPageItemFromWizardItem: Unexpected Page Item of Page ${pageName || '<unknown>'}`);
  }

  function LoadKamPageItem(this: void, wizardItem: NavPageItem<IFDRW_Wizard>, setraUser: boolean) {
    var key3 = wizardItem.GetValue("Key_3", true),
      key1 = <number>wizardItem.GetValue("Key_1", true),
      guid = wizardItem.GetValue("GUID", true),
      dlg = wizardItem.Anchor.Service.$c.resolve("DialogService");

    if (!key3)
      return <PromiseLike<NavPageItem | NavPageItems>>$c.when(undefined);
    else {
      // key but no guid? -> WTF!
      const instanceId = wizardItem.Anchor.InstanceID;
      const navUrl = wizardItem.ClientInfo.NavUrl;
      if (!guid)
        return $c.reject('Inkonsistente Vorkontierung: keine GUID, aber Key_3="' + key3 + '"');
      else {
        let constraints: IPageQueryConstraint[] = [{ FieldName: "DokID", Value: guid }];
        let pageName = { 3: FDRW_Auftrag, 2: FDRW_Auftragsbuchblatt, 1: FDRW_Anordnungsblatt }[key1];
        if (key1 == 2)
          constraints.push({ FieldName: "AktBuchblattName", Value: key3 });
        if (key1 == 3) {
          constraints.length = 0;
          constraints.push({ FieldName: "Nr", Value: key3 });
        }
        if (!pageName)
          return $c.reject("Invalid Value for 'Key_1'");
        const navClient = $c.resolve("NavClient");
        return navClient.GetPageClientInfo(pageName, navUrl).then(client => {
          // TODO : Resolve Assumption : ReadMultiple if "ForceReadMultiple" (so far = PageName === FDRW_Auftrag in kameral Scenario)
          if (client.MetaData.PageName === FDRW_Auftrag)
            return <PromiseLike<NavPageItem | NavPageItems>>navClient.LoadItem({ InstanceID: instanceId, NavUrl: navUrl, PageName: client.MetaData.PageName, Keys: fn.ToDictionaryEx(constraints, item => <string>item.FieldName, item => item.Value) }).then(item => {
              if (!item) {
                if (setraUser) {
                  return dlg.ShowError(MSG_MISSING_DATA_SETRA).then(() => item);
                }
                let info = constraints.map(c => c.FieldName + (c.Op || "=") + c.Value).join(",");
                throw new Error("FDRW_Auftrag [" + info + "] konnte nicht bestimmt werden (evtl. kein Recht?)!");
              }
              return item;
            });
          else
            return navClient.LoadPageItems({
              Constraints: constraints,
              PageCount: 0,
              PageName: pageName,
              InstanceID: instanceId
            }, navUrl).then(pageItemInfo => {
              if (pageItemInfo.Items.length == 0) {
                if (setraUser) {
                  return dlg.ShowError(MSG_MISSING_DATA_SETRA).then(() => undefined);
                }
                throw new Error('Inkonsistente Vorkontierung: keine Anordnung[' + pageName + '] "' + guid + '"');
              }
              pageItemInfo.Anchor.InstanceID = instanceId;
              return pageItemInfo;
            });
        });
      }
    }
  }

  function LoadDopPageItem(this: void, wizardItem: NavPageItem<IFDDI_Wizard_Create_New_DMS_Document>, setraUser: boolean) {
    let w = wizardItem.Model;
    let pageName = w.Key_1 == 0 ? FDDI_DMS_Sales_Document : FDDI_DMS_Purchase_Document,
      docType = w.Key_2,
      no = w.Key_3 || "";

    if (!no)
      return $c.when(<NavPageItem>undefined);

    return $c.resolve("NavClient").LoadItem({
      PageName: pageName, NavUrl: wizardItem.ClientInfo.Url,
      Keys: { Document_Type: docType, No: no },
      InstanceID: wizardItem.Anchor.InstanceID
    })
      .then(item => {
        if (!item) {
          if (setraUser) {
            return $c.resolve("DialogService").ShowError(MSG_MISSING_DATA_SETRA).then(() => undefined);
          }
          throw new Error("Inkonsistenter Vorkontierungseintrag: Beleg No='" + no + "', Document_Type='" + docType + "' ('" + pageName + "') konnte nicht bestimmt werden!");
        }
        return item;
      });
  }
}

export function ValidateFileSize(file: File, maxSizeInMB : number = 70) {
    var sizeInMB = (file.size / (1024 * 1024));
    if (sizeInMB >= maxSizeInMB) {
    throw new Error(`Die Datei "${file.name}" hat eine Größe von ${sizeInMB.toFixed(2)} MB und kann nicht importiert werden.\r\nDie maximal erlaubte Dateigröße beträgt ${maxSizeInMB.toFixed(0)} MB.`)
  }
}

export function toBase64(file: Blob) {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve((reader.result as string).split("base64,")[1] || reader.result as string);
    reader.onerror = error => reject(error);
  });
}
